import React, { useState, useRef } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Select from "../../../components/ui/Select";
import * as XLSX from "xlsx";
import { useAuth } from "../../../contexts/AuthContext";
import { regions as regionsAPI, clusters as clustersAPI } from "../../../lib/database";

const ExcelUploadModal = ({
  isOpen,
  onClose,
  onUpload,
  clusters = [],
  regions = [],
  projects = [],
}) => {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({
    current: 0,
    total: 0,
  });
  const [selectedProject, setSelectedProject] = useState(
    user?.project_id || ""
  );
  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedCluster, setSelectedCluster] = useState("");
  const [projectClusters, setProjectClusters] = useState([]);
  const fileInputRef = useRef(null);

  // Filter regions and clusters based on selected project
  const filteredRegions = React.useMemo(() => {
    if (!selectedProject) return [];
    return (
      regions?.filter((region) => region.project_id === selectedProject) || []
    );
  }, [selectedProject, regions]);

  const filteredClusters = React.useMemo(() => {
    if (!selectedProject) return [];
    return (
      clusters?.filter((cluster) => cluster.project_id === selectedProject) ||
      []
    );
  }, [selectedProject, clusters]);

  // Always fetch clusters for the currently selected project so the dropdown
  // doesn't depend on parent-scoped cluster data.
  React.useEffect(() => {
    let mounted = true;

    const loadProjectClusters = async () => {
      if (!selectedProject) {
        if (mounted) setProjectClusters([]);
        return;
      }

      try {
        const fetchedClusters =
          (await clustersAPI.getByProjectId(selectedProject)) || [];
        if (mounted) setProjectClusters(fetchedClusters);
      } catch (error) {
        console.error("Failed to load clusters for selected project:", error);
        if (mounted) {
          // Fallback to whatever cluster list parent already passed down.
          setProjectClusters(filteredClusters);
        }
      }
    };

    loadProjectClusters();
    return () => {
      mounted = false;
    };
  }, [selectedProject, filteredClusters]);

  // Reset region and cluster when project changes
  React.useEffect(() => {
    if (selectedProject) {
      const projectRegions =
        regions?.filter((r) => r.project_id === selectedProject) || [];
      if (
        selectedRegion &&
        !projectRegions.find((r) => r.id === selectedRegion)
      ) {
        setSelectedRegion("");
      }
      if (
        selectedCluster &&
        !projectClusters.find((c) => c.id === selectedCluster)
      ) {
        setSelectedCluster("");
      }
    } else {
      setSelectedRegion("");
      setSelectedCluster("");
    }
  }, [selectedProject, regions, projectClusters, selectedRegion, selectedCluster]);

  // Set default project for project_admin on mount
  React.useEffect(() => {
    if (user?.role === "project_admin" && user?.project_id) {
      setSelectedProject(user.project_id);
    }
  }, [user]);

  const projectOptions =
    projects?.map((project) => ({
      value: project.id,
      label: project.name,
    })) || [];

  const regionOptions =
    filteredRegions?.map((region) => ({
      value: region.id,
      label: region.name,
    })) || [];

  const clusterOptions =
    projectClusters?.map((cluster) => ({
      value: cluster.id,
      label: cluster.name,
    })) || [];

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);

    const droppedFile = e?.dataTransfer?.files?.[0];
    if (
      droppedFile &&
      (droppedFile?.name?.endsWith(".xlsx") ||
        droppedFile?.name?.endsWith(".xls") ||
        droppedFile?.name?.endsWith(".csv"))
    ) {
      setFile(droppedFile);
      setUploadStatus(null);
      setValidationErrors([]);
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e?.target?.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setUploadStatus(null);
      setValidationErrors([]);
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  const downloadTemplate = () => {
    // Template format: SERVICE_NO, CONSUMER_NAME, DISTRIBUSION, CATOGERY, CONTRACTED_LOAD, MOBILE, VILLAGE
    const headers = [
      "SERVICE_NO",
      "CONSUMER_NAME",
      "DISTRIBUSION",
      "CATOGERY",
      "CONTRACTED_LOAD",
      "MOBILE",
      "VILLAGE",
    ];

    const sampleRow = [
      "3611149000085", // SERVICE_NO (maps to usn)
      "Merlapaka. Vijaya", // CONSUMER_NAME (maps to name)
      "49-YERRASETTI PALEM(N449)", // DISTRIBUSION (optional)
      "LT1", // CATOGERY (optional)
      "1.26", // CONTRACTED_LOAD (optional)
      "9949760770", // MOBILE (maps to phone_number)
      "YERRASETTI PALEM", // VILLAGE (maps to village; region auto-created from this)
    ];

    const data = [headers, sampleRow];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(data);
    ws["!cols"] = [
      { wch: 18 }, // SERVICE_NO
      { wch: 25 }, // CONSUMER_NAME
      { wch: 28 }, // DISTRIBUSION
      { wch: 10 }, // CATOGERY
      { wch: 14 }, // CONTRACTED_LOAD
      { wch: 12 }, // MOBILE
      { wch: 20 }, // VILLAGE
    ];
    XLSX.utils.book_append_sheet(wb, ws, "Customers");
    XLSX.writeFile(wb, "customer_upload_template.csv", { bookType: "csv" });
  };

  const parseFile = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          let jsonData;

          if (file.name.endsWith(".csv")) {
            // Handle CSV files
            const text = e.target.result;
            const workbook = XLSX.read(text, {
              type: "string",
              FS: ",",
              RS: "\n",
            });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            jsonData = XLSX.utils.sheet_to_json(worksheet, {
              header: 1,
              defval: "",
            });
          } else {
            // Handle Excel files (.xlsx, .xls)
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: "array" });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            jsonData = XLSX.utils.sheet_to_json(worksheet, {
              header: 1,
              defval: "",
            });
          }

          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => {
        reject(new Error("Failed to read file"));
      };

      if (file.name.endsWith(".csv")) {
        reader.readAsText(file);
      } else {
        reader.readAsArrayBuffer(file);
      }
    });
  };

  // Helper function to get field value with multiple possible header names
  const getFieldValue = (rowData, possibleNames) => {
    for (const name of possibleNames) {
      // Try normalized version (no underscores, lowercase)
      const normalized = name
        .toLowerCase()
        .replace(/_/g, "")
        .replace(/\s/g, "");
      if (rowData[normalized] !== undefined && rowData[normalized] !== "") {
        return rowData[normalized];
      }
      // Try original name (lowercase)
      const lower = name.toLowerCase();
      if (rowData[lower] !== undefined && rowData[lower] !== "") {
        return rowData[lower];
      }
      // Try with underscores
      const withUnderscore = name.toLowerCase().replace(/\s/g, "_");
      if (
        rowData[withUnderscore] !== undefined &&
        rowData[withUnderscore] !== ""
      ) {
        return rowData[withUnderscore];
      }
    }
    return "";
  };

  const validateRow = (row, index, headers) => {
    const errors = [];
    const rowData = {};

    // Store both original and normalized header names for flexible lookup
    headers.forEach((header, colIndex) => {
      const value = row[colIndex]?.toString()?.trim() || "";
      const originalHeader = header?.toString()?.trim() || "";
      const normalizedHeader = originalHeader
        .toLowerCase()
        .replace(/_/g, "")
        .replace(/\s/g, "");

      // Store with both original and normalized keys
      rowData[originalHeader.toLowerCase()] = value;
      rowData[normalizedHeader] = value;
      // Also store with underscores replaced by spaces
      rowData[originalHeader.toLowerCase().replace(/_/g, " ")] = value;
    });

    // Get consumer_name with multiple possible variations (CONSUMER_NAME in new format)
    const consumerName = getFieldValue(rowData, [
      "consumer_name",
      "consumer name",
      "consumername",
      "name",
      "customer_name",
      "customer name",
      "customername",
    ]);

    // consumer_name is optional - no validation needed

    // Get mobile with multiple possible variations (OPTIONAL)
    const mobileRaw = getFieldValue(rowData, [
      "mobile",
      "phone_number",
      "phone number",
      "phonenumber",
      "phone",
      "contact_number",
      "contact number",
      "contactnumber",
    ]);

    // Validate aadhar if present - it can be used as the only identifier
    const aadharRaw = getFieldValue(rowData, ["aadhar"]);

    if (aadharRaw) {
      const aadhar = aadharRaw.replace(/\D/g, "");
      if (aadhar.length > 0 && aadhar.length !== 12) {
        errors.push(
          `Row ${index + 2}: aadhar must be 12 digits (found ${
            aadhar.length
          } digits)`
        );
      }
    }

    // At least one identifier is needed: consumer_name, mobile, or valid aadhar
    // But we don't validate this here - convertToCustomerData will handle it
    // This allows flexible data where any one field can identify the customer

    return { errors, rowData };
  };

  const convertToCustomerData = (rowData, headers) => {
    // rowData already has multiple key variations from validateRow
    // Use the flexible rowData for lookups
    const getValue = (possibleNames) => getFieldValue(rowData, possibleNames);

    // Map CSV fields to database fields with flexible name matching
    // CONSUMER_NAME / consumer_name → name
    const name = getValue([
      "consumer_name",
      "consumer name",
      "consumername",
      "name",
      "customer_name",
      "customer name",
      "customername",
    ]);

    // mobile → phone_number
    const mobileRaw = getValue([
      "mobile",
      "phone_number",
      "phone number",
      "phonenumber",
      "phone",
      "contact_number",
      "contact number",
    ]);
    // Extract digits and take last 10 if more than 10 (handles country codes)
    const phoneNumber = mobileRaw.replace(/\D/g, "");
    const cleanedPhone =
      phoneNumber.length > 10 ? phoneNumber.slice(-10) : phoneNumber;

    // aadhar → aadhaar_number
    const aadharRaw = getValue(["aadhar"]);
    const aadhaarNumber = aadharRaw ? aadharRaw.replace(/\D/g, "") : null;
    // Validate aadhaar number - must be 12 digits if provided
    const validAadhaar =
      aadhaarNumber && aadhaarNumber.length === 12 ? aadhaarNumber : null;

    // Combine doorno + street → address
    const doorno = getValue(["doorno", "door_no", "door no", "doorno"]);
    const street = getValue(["street", "street_name", "street name"]);
    const addressParts = [doorno, street].filter(Boolean);
    const address = addressParts.length > 0 ? addressParts.join(", ") : null;

    // village → village
    const village = getValue(["village"]);

    // district → district
    const district = getValue(["district", "dist"]);

    // service_number / SERVICE_NO → usn (User Service Number from Excel/govt)
    const serviceNumber = getValue([
      "service_no",
      "serviceno",
      "service_number",
      "service number",
      "servicenumber",
      "usn",
      "govt_sn",
      "govt sn",
      "govtsn",
    ]);

    // State defaults to "Andhra Pradesh"
    const stateRaw = getValue(["state", "state_name", "state name"]);
    const state = stateRaw || "Andhra Pradesh";

    // pincode
    const pincodeRaw = getValue([
      "pincode",
      "pin_code",
      "pin code",
      "postal_code",
      "postal code",
    ]);
    const pincode = pincodeRaw ? pincodeRaw.replace(/\D/g, "") : null;

    // Create customer record if at least one of: name, phone, or valid aadhaar exists
    if (!name && !cleanedPhone && !validAadhaar) {
      return null;
    }

    // Database requires both name and phone_number to be NOT NULL
    // Set placeholder values if missing (we have at least aadhaar to identify the customer)
    const customer = {
      name:
        name ||
        (validAadhaar
          ? `Customer-${validAadhaar.slice(-4)}`
          : "Unknown Customer"),
      phone_number: cleanedPhone || "0000000000",
      project_id: selectedProject || null,
      region_id: selectedRegion || null,
      cluster_id: selectedCluster || null, // Required - validated before upload
      status: "survey_not_started", // Default status
      state: state,
    };

    // Optional fields - only add if they have values
    if (validAadhaar) {
      customer.aadhaar_number = validAadhaar;
    }
    if (address) {
      customer.address = address;
    }
    if (village) {
      customer.village = village;
    }
    if (district) {
      customer.district = district;
    }
    if (pincode && pincode.length > 0) {
      customer.pincode = pincode;
    }
    if (serviceNumber) {
      customer.usn = serviceNumber.trim();
    }

    // Convert empty strings to null
    Object.keys(customer).forEach((key) => {
      if (customer[key] === "") {
        customer[key] = null;
      }
    });

    return customer;
  };

  const handleUpload = async () => {
    if (!file) return;

    // Validate project and cluster are selected before processing
    if (!selectedProject) {
      setValidationErrors(["Please select a project"]);
      setUploadStatus("error");
      return;
    }

    if (!selectedCluster) {
      setValidationErrors(["Please select a cluster"]);
      setUploadStatus("error");
      return;
    }

    setUploadStatus("processing");
    setValidationErrors([]);

    try {
      // Parse the file
      const data = await parseFile(file);

      if (!data || data.length < 2) {
        throw new Error("File is empty or invalid format");
      }

      // First row should be headers
      const headers = data[0].map((h) => h?.toString()?.trim() || "");
      const rows = data.slice(1);

      // Validate all rows first
      const allErrors = [];
      const validCustomers = [];

      rows.forEach((row, index) => {
        const { errors, rowData } = validateRow(row, index, headers);
        if (errors.length > 0) {
          allErrors.push(...errors);
        } else {
          const customer = convertToCustomerData(rowData, headers);
          // Only add customer if it's not null (has at least name or phone_number)
          if (customer !== null) {
            validCustomers.push(customer);
          }
        }
      });

      if (allErrors.length > 0) {
        setValidationErrors(allErrors);
        setUploadStatus("error");
        return;
      }

      if (validCustomers.length === 0) {
        setValidationErrors(["No valid customer records found"]);
        setUploadStatus("error");
        return;
      }

      // Map village -> region. If region doesn't exist, create it in Supabase,
      // then assign the customer to that region before upload.
      const regionNameToId = new Map();

      // Seed with already loaded regions for this project
      (regions || []).forEach((region) => {
        if (region?.name) {
          regionNameToId.set(region.name.trim().toLowerCase(), region.id);
        }
      });

      // Ensure each customer's village has a corresponding region
      for (const customer of validCustomers) {
        const villageName = customer?.village?.trim();

        // If village not present, fall back to the selectedRegion (if any)
        if (!villageName) {
          if (!customer.region_id && selectedRegion) {
            customer.region_id = selectedRegion;
          }
          continue;
        }

        const key = villageName.toLowerCase();
        let regionId = regionNameToId.get(key);

        if (!regionId) {
          // Create a new region for this village name under the selected project
          const newRegion = await regionsAPI.create({
            name: villageName,
            project_id: selectedProject,
          });
          regionId = newRegion.id;
          regionNameToId.set(key, regionId);
        }

        customer.region_id = regionId;
      }

      // Upload valid customers to Supabase
      setUploadProgress({ current: 0, total: validCustomers.length });

      let uploadedCount = 0;
      for (const customer of validCustomers) {
        await onUpload([customer]); // Upload one at a time for better error handling
        uploadedCount += 1;
        setUploadProgress({
          current: uploadedCount,
          total: validCustomers.length,
        });
      }

      setUploadStatus("success");
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error) {
      console.error("Upload error:", error);
      setValidationErrors([error.message || "Failed to process file"]);
      setUploadStatus("error");
    }
  };

  const handleClose = () => {
    setFile(null);
    setUploadStatus(null);
    setValidationErrors([]);
    setUploadProgress({ current: 0, total: 0 });
    setSelectedRegion("");
    setSelectedCluster("");
    // Reset project only if super_admin, keep for project_admin
    if (user?.role === "super_admin") {
      setSelectedProject("");
    } else if (user?.role === "project_admin" && user?.project_id) {
      setSelectedProject(user.project_id);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Upload" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                Upload Customer Data
              </h2>
              <p className="text-sm text-muted-foreground">
                Import customer records from Excel/CSV file
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
            disabled={uploadStatus === "processing"}
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Project and Region Selection */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-foreground">
              Select Project & Region
            </h3>

            {/* Project Selection */}
            <div className="space-y-2">
              <label className="text-xs text-muted-foreground">
                Project {user?.role === "super_admin" ? "*" : ""}
              </label>
              {user?.role === "super_admin" ? (
                <Select
                  options={projectOptions}
                  value={selectedProject}
                  onChange={setSelectedProject}
                  placeholder="Select project"
                  disabled={uploadStatus === "processing"}
                  searchable
                />
              ) : user?.role === "project_admin" && user?.project_id ? (
                <div className="p-3 bg-muted rounded-lg text-sm text-foreground">
                  {projects?.find((p) => p.id === user.project_id)?.name ||
                    "Current Project"}
                </div>
              ) : null}
            </div>

            {/* Region and Cluster Selection - Same Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Region Selection (Optional) */}
              <div className="space-y-2">
                <label className="text-xs text-muted-foreground">
                  Region (Optional)
                </label>
                <Select
                  options={regionOptions}
                  value={selectedRegion}
                  onChange={setSelectedRegion}
                  placeholder={
                    selectedProject ? "Select region" : "Select project first"
                  }
                  disabled={!selectedProject || uploadStatus === "processing"}
                  searchable
                />
                <p className="text-xs text-muted-foreground/80 flex items-center gap-1">
                  <Icon name="Info" size={12} />
                  Auto-created from village field if not selected
                </p>
              </div>

              {/* Cluster Selection (Required) */}
              <div className="space-y-2">
                <label className="text-xs text-muted-foreground">
                  Cluster *
                </label>
                <Select
                  options={clusterOptions}
                  value={selectedCluster}
                  onChange={setSelectedCluster}
                  placeholder={
                    selectedProject
                      ? "Select cluster"
                      : "Select project first"
                  }
                  disabled={!selectedProject || uploadStatus === "processing"}
                  searchable
                />
              </div>
            </div>
          </div>

          {/* Template Download */}
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div className="flex items-center gap-3">
              <Icon name="FileSpreadsheet" size={20} className="text-primary" />
              <div>
                <p className="text-sm font-medium text-foreground">
                  Download Template
                </p>
                <p className="text-xs text-muted-foreground">
                  Use this template to ensure correct format
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              iconPosition="left"
              onClick={downloadTemplate}
              disabled={uploadStatus === "processing"}
            >
              Download Template
            </Button>
          </div>

          {/* Upload Area */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDragging
                ? "border-primary bg-primary/5"
                : "border-border hover:border-primary/50"
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileSelect}
              className="hidden"
              disabled={uploadStatus === "processing"}
            />

            {!file ? (
              <>
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                  <Icon
                    name="FileSpreadsheet"
                    size={32}
                    className="text-muted-foreground"
                  />
                </div>
                <h3 className="text-base font-medium text-foreground mb-2">
                  Drop your Excel/CSV file here
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  or click to browse from your computer
                </p>
                <Button
                  variant="outline"
                  onClick={() => fileInputRef?.current?.click()}
                  disabled={uploadStatus === "processing"}
                >
                  Select File
                </Button>
                <p className="text-xs text-muted-foreground mt-4">
                  Supported formats: .xlsx, .xls, .csv (Max 10MB)
                </p>
              </>
            ) : (
              <div className="flex items-center justify-between bg-muted rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon
                      name="FileSpreadsheet"
                      size={20}
                      className="text-primary"
                    />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-foreground">
                      {file?.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {(file?.size / 1024)?.toFixed(2)} KB
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setFile(null)}
                  className="w-8 h-8 rounded-lg hover:bg-background flex items-center justify-center transition-colors"
                  disabled={uploadStatus === "processing"}
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
            )}
          </div>

          {/* Upload Progress */}
          {uploadStatus === "processing" && uploadProgress.total > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Uploading...</span>
                <span className="text-foreground font-medium">
                  {uploadProgress.current} / {uploadProgress.total}
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${
                      (uploadProgress.current / uploadProgress.total) * 100
                    }%`,
                  }}
                />
              </div>
            </div>
          )}

          {/* Status Messages */}
          {uploadStatus === "processing" && uploadProgress.total === 0 && (
            <div className="flex items-center gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <Icon
                name="Loader2"
                size={20}
                className="text-blue-600 dark:text-blue-400 animate-spin"
              />
              <div>
                <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  Processing file...
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  Validating customer records
                </p>
              </div>
            </div>
          )}

          {uploadStatus === "success" && (
            <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <Icon
                name="CheckCircle2"
                size={20}
                className="text-green-600 dark:text-green-400"
              />
              <div>
                <p className="text-sm font-medium text-green-900 dark:text-green-100">
                  Upload successful!
                </p>
                <p className="text-xs text-green-700 dark:text-green-300">
                  {uploadProgress.total} customer records imported successfully
                </p>
              </div>
            </div>
          )}

          {uploadStatus === "error" && validationErrors?.length > 0 && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-start gap-3 mb-3">
                <Icon
                  name="AlertCircle"
                  size={20}
                  className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5"
                />
                <div>
                  <p className="text-sm font-medium text-red-900 dark:text-red-100">
                    Validation errors found
                  </p>
                  <p className="text-xs text-red-700 dark:text-red-300">
                    Please fix the following issues:
                  </p>
                </div>
              </div>
              <ul className="space-y-1 ml-8 max-h-48 overflow-y-auto">
                {validationErrors?.slice(0, 20).map((error, index) => (
                  <li
                    key={index}
                    className="text-xs text-red-700 dark:text-red-300"
                  >
                    • {error}
                  </li>
                ))}
                {validationErrors?.length > 20 && (
                  <li className="text-xs text-red-700 dark:text-red-300 font-medium">
                    ... and {validationErrors.length - 20} more errors
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={uploadStatus === "processing"}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleUpload}
            disabled={!file || uploadStatus === "processing"}
            loading={uploadStatus === "processing"}
            iconName="Upload"
            iconPosition="left"
          >
            Upload Data
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExcelUploadModal;
